import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import './Modal.css';

/**
 * Универсальная SaaS-модалка для всего проекта.
 *
 * Использование:
 *   <Modal isOpen={isOpen} onClose={onClose} title="..." subtitle="..." size="md|lg|xl" footer={...}>
 *     ...тело
 *   </Modal>
 *
 * Размеры:  sm (380px) | md (520px) | lg (720px) | xl (960px) | full (95vw)
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  closable = true,
  className = '',
  children,
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && closable) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, closable, onClose]);

  if (!isOpen) return null;

  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget && closable) onClose?.();
  };

  return createPortal(
    <div className="uimodal-overlay" onClick={onOverlayClick} role="presentation">
      <div
        className={`uimodal uimodal--${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'uimodal-title' : undefined}
      >
        {(title || closable) && (
          <header className="uimodal__head">
            <div className="uimodal__head-text">
              {title && (
                <h2 id="uimodal-title" className="uimodal__title">{title}</h2>
              )}
              {subtitle && <p className="uimodal__subtitle">{subtitle}</p>}
            </div>
            {closable && (
              <button
                type="button"
                className="uimodal__close"
                onClick={onClose}
                aria-label="Закрыть"
              ><CloseIcon sx={{ fontSize: 20 }} /></button>
            )}
          </header>
        )}

        <div className="uimodal__body">
          {children}
        </div>

        {footer && (
          <footer className="uimodal__foot">{footer}</footer>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Готовые кнопки для футера модалок
 */
export function ModalButton({ variant = 'secondary', onClick, type = 'button', disabled, children, autoFocus }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      autoFocus={autoFocus}
      className={`uimodal-btn uimodal-btn--${variant}`}
    >{children}</button>
  );
}
